import React, { useCallback, useEffect, useRef, useState } from 'react'
import { MAX_SCREENSHOT_SIZE } from '@common/constants/client'
import { rejectWithTimeLimit } from '@common/helpers/helpers'
import {
  grabViewportFrame,
  isTabCaptureSupported,
  ITabStream,
  openTabStream,
  waitForFreshFrame,
} from './tabCapture'

export interface ISelectionRect {
  x: number
  y: number
  width: number
  height: number
}

interface IPageRender {
  canvas: HTMLCanvasElement
  // Canvas pixels per CSS pixel.
  scale: number
}

interface IScreenshots {
  full: string | null
  region: string | null
}

// Hard ceiling on a single capture. Rendering the DOM to a canvas cannot be cancelled, so
// this stops the *form* waiting on it rather than the work itself. The loan committee report
// takes ~26s in Firefox, and a limit under that discards every render of it.
const CAPTURE_TIMEOUT_MS = 45000

// How long modern-screenshot may spend on one remote asset (image, web font), and on decoding
// the final page image — past it, that image is drawn undecoded and the screenshot comes out blank.
const ASSET_TIMEOUT_MS = 15000

// How long a new panel session waits out a previous session's render before assuming it hung and
// rendering anyway. Far above any real render, so a slow one is never overlapped by a second.
const STALE_RENDER_TIMEOUT_MS = 90000

// Past this many elements, copying the DOM freezes the tab for longer than an automatic screenshot
// is worth: the loan committee report has ~21k and takes over 45s. Only Submit's capture skips it.
const MAX_AUTO_CAPTURE_ELEMENTS = 5000

export const isPageTooLargeToAutoCapture = (): boolean =>
  document.body.getElementsByTagName('*').length > MAX_AUTO_CAPTURE_ELEMENTS

// A page too large to render can only be captured from a share of the tab.
export const canCaptureRegion = (): boolean =>
  !isPageTooLargeToAutoCapture() || isTabCaptureSupported()

// Largest canvas one page render may allocate (~64MB of RGBA); a bigger page renders smaller.
const MAX_RENDER_PIXELS = 4096 * 4096

// An incompressible PNG costs 4 bytes a pixel plus row filters and chunk headers.
const PNG_WORST_CASE_BYTES_PER_PIXEL = 4.5

// Most pixels a screenshot can hold and still fit MAX_SCREENSHOT_SIZE however badly it compresses.
const MAX_SCREENSHOT_PIXELS = Math.floor(MAX_SCREENSHOT_SIZE / PNG_WORST_CASE_BYTES_PER_PIXEL)

const NO_SCREENSHOTS: IScreenshots = { full: null, region: null }

// Excludes the widget's own DOM (launcher / modal / region overlay) from screenshots.
const screenshotFilter = (node: Node): boolean =>
  !(node instanceof HTMLElement && node.dataset.feedbackWidget !== undefined)

// Lets the browser paint before the capture seizes the main thread — otherwise the loading
// overlay or the "Capturing screenshot…" placeholder does not appear until it finishes.
const yieldToPaint = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => setTimeout(resolve, 0))
  })

/**
 * Largest scale (at most 1:1) at which neither the page canvas nor a viewport crop of it goes
 * over budget. Decided before rendering, so an oversized canvas or PNG is never built.
 */
const renderScaleFor = (pageWidth: number, pageHeight: number): number =>
  Math.min(
    1,
    Math.sqrt(MAX_RENDER_PIXELS / (pageWidth * pageHeight)),
    Math.sqrt(MAX_SCREENSHOT_PIXELS / (window.innerWidth * window.innerHeight)),
  )

// Renders document.body to a canvas, excluding the widget.
const renderPage = async (): Promise<IPageRender | null> => {
  await yieldToPaint()
  // The box modern-screenshot sizes its canvas from.
  const { width, height } = document.body.getBoundingClientRect()
  if (width <= 0 || height <= 0) {
    return null
  }
  // Loaded on first capture: the widget is on every page, the screenshot library is not needed there.
  const { domToCanvas } = await import('modern-screenshot')
  const canvas = await domToCanvas(document.body, {
    filter: screenshotFilter,
    backgroundColor: '#ffffff',
    timeout: ASSET_TIMEOUT_MS,
    scale: renderScaleFor(width, height),
    // The app scrolls inner containers (e.g. MainLayout's `.content`), not the window, so
    // without this every scrolled container renders from its top instead of what the user sees.
    // A no-op at scroll 0.
    features: { restoreScrollPosition: true },
  })
  // Derived from the rendered canvas rather than the requested scale, which it may round.
  return { canvas, scale: canvas.width / width }
}

const cropCanvas = (
  { canvas: source, scale }: IPageRender,
  rect: ISelectionRect,
): HTMLCanvasElement | null => {
  const sw = Math.round(rect.width * scale)
  const sh = Math.round(rect.height * scale)
  if (sw <= 0 || sh <= 0) {
    return null
  }
  const canvas = document.createElement('canvas')
  canvas.width = sw
  canvas.height = sh
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return null
  }
  ctx.drawImage(
    source,
    Math.round(rect.x * scale),
    Math.round(rect.y * scale),
    sw,
    sh,
    0,
    0,
    sw,
    sh,
  )
  return canvas
}

const cropCanvasToDataUrl = (page: IPageRender, rect: ISelectionRect): string | null =>
  cropCanvas(page, rect)?.toDataURL('image/png') ?? null

const viewportRect = (origin: { x: number; y: number }): ISelectionRect => ({
  x: origin.x,
  y: origin.y,
  width: window.innerWidth,
  height: window.innerHeight,
})

// One capture of the visible viewport, for callers outside the widget. Null when it fails.
export const captureViewportCanvas = async (): Promise<HTMLCanvasElement | null> => {
  try {
    const page = await rejectWithTimeLimit(CAPTURE_TIMEOUT_MS, renderPage())
    return page ? cropCanvas(page, viewportRect({ x: window.scrollX, y: window.scrollY })) : null
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Screenshot: viewport capture failed', error)
    return null
  }
}

// Crops the visible viewport, plus the dragged region if there is one. `origin` is where the
// viewport sits in the canvas: window scroll for a whole-page render (restoreScrollPosition covers
// inner-container scroll), the top-left corner for a frame of the tab itself.
const cropScreenshots = (
  page: IPageRender,
  region?: ISelectionRect,
  origin = { x: window.scrollX, y: window.scrollY },
): IScreenshots => ({
  full: cropCanvasToDataUrl(page, viewportRect(origin)),
  region: region
    ? cropCanvasToDataUrl(page, {
        x: origin.x + region.x,
        y: origin.y + region.y,
        width: region.width,
        height: region.height,
      })
    : null,
})

// Handles screenshot capture for the feedback widget: the full-viewport screenshot attached on
// submit plus the optional drag-to-select region capture.
const useScreenshotCapture = () => {
  const [isSelectingRegion, setIsSelectingRegion] = useState(false)
  const [isCapturingRegion, setIsCapturingRegion] = useState(false)
  const [dragRect, setDragRect] = useState<ISelectionRect | null>(null)
  const [regionScreenshot, setRegionScreenshot] = useState<string | null>(null)
  // Where on screen the region screenshot was taken from, in viewport CSS pixels.
  const [regionRect, setRegionRect] = useState<ISelectionRect | null>(null)
  const [hasRegionCaptureFailed, setHasRegionCaptureFailed] = useState(false)
  // True while a frame of the tab is being taken, so the widget can get out of the picture.
  const [isGrabbingFrame, setIsGrabbingFrame] = useState(false)

  const fullScreenshotRef = useRef<string | null>(null)
  const dragStartRef = useRef<{ x: number; y: number } | null>(null)
  // Bumped by resetScreenshots, so a capture started before the reset can tell that its
  // canvas is of a screen the user has since left and drop it instead of storing it.
  const captureEpochRef = useRef(0)
  // The render still running, if any. Outlives resets and timeouts: the work cannot be stopped,
  // and a second render alongside it is what exhausts the tab on a dense page.
  const renderInFlightRef = useRef<{ epoch: number; render: Promise<IPageRender | null> } | null>(
    null,
  )
  // A capture in flight cannot be cancelled, so it can still settle after the
  // widget has gone. The epoch guards against a stale capture landing over a
  // newer one; this guards against one landing at all, which otherwise reaches
  // a React root that no longer exists.
  const isMountedRef = useRef(true)
  // The tab share opened for a region grab on a page too large to render, until the grab ends.
  const tabStreamRef = useRef<ITabStream | null>(null)

  const takeTabStream = useCallback((): ITabStream | null => {
    const stream = tabStreamRef.current
    tabStreamRef.current = null
    return stream
  }, [])

  useEffect(
    () => () => {
      isMountedRef.current = false
      takeTabStream()?.stop()
    },
    [takeTabStream],
  )

  /**
   * Joins the render already running for this epoch. One left over from an earlier epoch is of a
   * page the user has left, so it is waited out and a fresh render started once it finishes — or
   * dropped once it has had STALE_RENDER_TIMEOUT_MS, so one hung render cannot disable capture.
   */
  const renderOnce = useCallback(async (epoch: number): Promise<IPageRender | null> => {
    while (renderInFlightRef.current && renderInFlightRef.current.epoch !== epoch) {
      const stale = renderInFlightRef.current
      await rejectWithTimeLimit(STALE_RENDER_TIMEOUT_MS, stale.render).catch(() => {
        if (renderInFlightRef.current === stale) {
          renderInFlightRef.current = null
        }
      })
    }
    // Reset during the wait: nobody would use this render, and it would block the next session.
    if (epoch !== captureEpochRef.current) {
      return null
    }
    if (renderInFlightRef.current) {
      return renderInFlightRef.current.render
    }
    const render = renderPage().catch((error: unknown): null => {
      // eslint-disable-next-line no-console
      console.error('Feedback screenshot: page render failed', error)
      return null
    })
    const inFlight = { epoch, render }
    renderInFlightRef.current = inFlight
    // Registered first, so it has cleared the slot before any waiter's `await` resumes.
    void inFlight.render.then(() => {
      if (renderInFlightRef.current === inFlight) {
        renderInFlightRef.current = null
      }
    })
    return inFlight.render
  }, [])

  const captureScreenshots = useCallback(
    async (epoch: number, region?: ISelectionRect): Promise<IScreenshots> => {
      const startedAt = Date.now()
      try {
        const page = await rejectWithTimeLimit(CAPTURE_TIMEOUT_MS, renderOnce(epoch))
        return page ? cropScreenshots(page, region) : NO_SCREENSHOTS
      } catch (error) {
        // A timeout or a failed crop: the report goes without a screenshot.
        // eslint-disable-next-line no-console
        console.error(
          `Feedback screenshot: capture failed after ${Date.now() - startedAt}ms`,
          error,
        )
        return NO_SCREENSHOTS
      }
    },
    [renderOnce],
  )

  // Captures the whole screen unless a region grab already has, or the page is too large to render.
  const captureFullScreenshot = useCallback(async (): Promise<void> => {
    if (fullScreenshotRef.current || isPageTooLargeToAutoCapture()) {
      return
    }
    const epoch = captureEpochRef.current
    const { full } = await captureScreenshots(epoch)
    if (epoch === captureEpochRef.current && !fullScreenshotRef.current) {
      fullScreenshotRef.current = full
    }
  }, [captureScreenshots])

  // Frames the tab from its own pixels, with the widget hidden, and crops the region from that.
  const captureFromTabStream = useCallback(
    async (stream: ITabStream, rect: ISelectionRect): Promise<IScreenshots> => {
      setIsGrabbingFrame(true)
      try {
        await waitForFreshFrame()
        const frame = grabViewportFrame(stream.video, MAX_SCREENSHOT_PIXELS)
        if (!frame) {
          // eslint-disable-next-line no-console
          console.error('Feedback screenshot: the shared screen is empty or is not this tab')
          return NO_SCREENSHOTS
        }
        return cropScreenshots(frame, rect, { x: 0, y: 0 })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Feedback screenshot: tab capture failed', error)
        return NO_SCREENSHOTS
      } finally {
        stream.stop()
        if (isMountedRef.current) {
          setIsGrabbingFrame(false)
        }
      }
    },
    [],
  )

  // A page too large to render is captured from a share of the tab instead, which the browser
  // asks permission for — so it is opened here, on the click, before the user drags.
  const startSelectingRegion = useCallback(async () => {
    setHasRegionCaptureFailed(false)
    if (!canCaptureRegion()) {
      return
    }
    if (isPageTooLargeToAutoCapture()) {
      const epoch = captureEpochRef.current
      try {
        const stream = await openTabStream()
        if (epoch !== captureEpochRef.current || !isMountedRef.current) {
          stream.stop()
          return
        }
        tabStreamRef.current = stream
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Feedback screenshot: screen share was not started', error)
        if (isMountedRef.current) {
          setHasRegionCaptureFailed(true)
        }
        return
      }
    }
    setIsSelectingRegion(true)
  }, [])

  const clearRegionScreenshot = useCallback(() => {
    setRegionScreenshot(null)
    setRegionRect(null)
    setHasRegionCaptureFailed(false)
  }, [])

  const handleRegionMouseDown = useCallback((e: React.MouseEvent) => {
    dragStartRef.current = { x: e.clientX, y: e.clientY }
    setDragRect({ x: e.clientX, y: e.clientY, width: 0, height: 0 })
  }, [])

  const handleRegionMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragStartRef.current) return
    const start = dragStartRef.current
    setDragRect({
      x: Math.min(start.x, e.clientX),
      y: Math.min(start.y, e.clientY),
      width: Math.abs(e.clientX - start.x),
      height: Math.abs(e.clientY - start.y),
    })
  }, [])

  const handleRegionMouseUp = useCallback(async () => {
    const rect = dragRect
    const stream = takeTabStream()
    dragStartRef.current = null
    setIsSelectingRegion(false)
    setDragRect(null)
    if (!rect || rect.width < 5 || rect.height < 5) {
      stream?.stop()
      return
    }
    // Rendering the DOM to canvas takes a moment — show a placeholder in the
    // preview slot so the user can see the capture is in flight.
    setIsCapturingRegion(true)
    setHasRegionCaptureFailed(false)
    const epoch = captureEpochRef.current
    try {
      const { full, region } = stream
        ? await captureFromTabStream(stream, rect)
        : await captureScreenshots(epoch, rect)
      if (epoch !== captureEpochRef.current || !isMountedRef.current) {
        return
      }
      // Only overwrite with something: a capture that failed or timed out returns nulls, and
      // an earlier region grab may already have left a good full screenshot.
      if (full) {
        fullScreenshotRef.current = full
      }
      setRegionScreenshot(region)
      setRegionRect(region ? rect : null)
      setHasRegionCaptureFailed(!region)
    } finally {
      if (epoch === captureEpochRef.current && isMountedRef.current) {
        setIsCapturingRegion(false)
      }
    }
  }, [dragRect, takeTabStream, captureFromTabStream, captureScreenshots])

  const resetScreenshots = useCallback(() => {
    setIsSelectingRegion(false)
    setDragRect(null)
    setRegionScreenshot(null)
    setRegionRect(null)
    setHasRegionCaptureFailed(false)
    fullScreenshotRef.current = null
    // Abandon any capture still running: reopening the panel from another page would
    // otherwise join it and attach a screenshot of the page the user has left.
    captureEpochRef.current += 1
    setIsCapturingRegion(false)
    takeTabStream()?.stop()
    setIsGrabbingFrame(false)
  }, [takeTabStream])

  return {
    isSelectingRegion,
    isCapturingRegion,
    dragRect,
    regionScreenshot,
    regionRect,
    hasRegionCaptureFailed,
    isGrabbingFrame,
    fullScreenshotRef,
    captureFullScreenshot,
    startSelectingRegion,
    clearRegionScreenshot,
    handleRegionMouseDown,
    handleRegionMouseMove,
    handleRegionMouseUp,
    resetScreenshots,
  }
}

export default useScreenshotCapture
